import type { HttpRequest, HttpResponse, KeyValue } from '../../stores';

export interface HttpClientConfig {
  proxyUrl?: string;
  getAccessToken?: () => Promise<string | null>;
  timeout?: number;
}

export interface ExecuteRequestOptions {
  request: HttpRequest;
  timeout?: number;
}

interface ProxyRequestBody {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: string;
  timeout?: number;
}

/**
 * Detect if we're running in Electron with the snailmanAPI IPC bridge available.
 * Only returns true if snailmanAPI.httpRequest is callable — the user agent
 * alone is not sufficient since the preload script may not inject the bridge
 * when loading remote URLs.
 */
export function isElectron(): boolean {
  if (typeof window !== 'undefined') {
    const api = (window as any).snailmanAPI;
    if (api?.isElectron && typeof api.httpRequest === 'function') {
      return true;
    }
  }
  return false;
}

/**
 * Convert KeyValue array to Record object, filtering enabled items
 * @internal Exported for testing
 */
export function keyValueToRecord(items: KeyValue[]): Record<string, string> {
  const result: Record<string, string> = {};
  items
    .filter((item) => item.enabled && item.key)
    .forEach((item) => {
      result[item.key] = item.value;
    });
  return result;
}

/**
 * Build the complete URL with query parameters
 * @internal Exported for testing
 */
export function buildUrl(request: HttpRequest): string {
  let url = request.url;

  const params = request.params.filter((p) => p.enabled && p.key);

  // Add API key to query if configured
  if (request.auth?.type === 'api-key' && request.auth.apiKey?.addTo === 'query') {
    params.push({
      key: request.auth.apiKey.key,
      value: request.auth.apiKey.value,
      enabled: true,
    });
  }

  if (params.length > 0) {
    const searchParams = new URLSearchParams();
    params.forEach((p) => searchParams.append(p.key, p.value));
    url = `${url}${url.includes('?') ? '&' : '?'}${searchParams.toString()}`;
  }

  return url;
}

/**
 * Build headers from request, including auth headers
 * @internal Exported for testing
 */
export function buildHeaders(request: HttpRequest): Record<string, string> {
  const headers = keyValueToRecord(request.headers);

  // Add auth headers
  if (request.auth?.type === 'bearer' && request.auth.bearer?.token) {
    headers['Authorization'] = `Bearer ${request.auth.bearer.token}`;
  } else if (request.auth?.type === 'basic' && request.auth.basic) {
    const { username, password } = request.auth.basic;
    headers['Authorization'] = `Basic ${btoa(`${username}:${password}`)}`;
  } else if (request.auth?.type === 'api-key' && request.auth.apiKey?.addTo === 'header') {
    headers[request.auth.apiKey.key] = request.auth.apiKey.value;
  }

  return headers;
}

/**
 * Get request body and set content-type if needed
 * @internal Exported for testing
 */
export function getRequestBody(request: HttpRequest, headers: Record<string, string>): string | undefined {
  if (request.body?.type === 'raw' && request.body.raw?.content) {
    // Set content-type if not already set
    if (!headers['Content-Type'] && !headers['content-type']) {
      const contentTypes: Record<string, string> = {
        json: 'application/json',
        xml: 'application/xml',
        text: 'text/plain',
      };
      headers['Content-Type'] = contentTypes[request.body.raw.contentType] || 'text/plain';
    }
    return request.body.raw.content;
  }

  if (request.body?.type === 'x-www-form-urlencoded' && request.body.formData) {
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    const params = new URLSearchParams();
    request.body.formData
      .filter((item) => item.enabled && item.key)
      .forEach((item) => params.append(item.key, item.value));
    return params.toString();
  }

  return undefined;
}

/**
 * Execute request directly via Electron's IPC (bypasses CORS)
 */
async function executeDirectRequest(
  request: HttpRequest,
  timeout: number
): Promise<HttpResponse> {
  const url = buildUrl(request);
  const headers = buildHeaders(request);
  const body = getRequestBody(request, headers);

  try {
    const result = await (window as any).snailmanAPI.httpRequest({
      method: request.method,
      url,
      headers,
      body: ['GET', 'HEAD'].includes(request.method) ? undefined : body,
      timeout,
    });

    if (result.error) {
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: `Request failed: ${result.error}`,
        size: 0,
        time: result.responseTime || 0,
      };
    }

    return {
      status: result.status,
      statusText: result.statusText,
      headers: result.headers,
      body: result.body,
      size: new TextEncoder().encode(result.body).length,
      time: result.responseTime,
    };
  } catch (err) {
    return {
      status: 0,
      statusText: 'Error',
      headers: {},
      body: `Request failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      size: 0,
      time: 0,
    };
  }
}

/**
 * Execute request via proxy (for web browser)
 */
async function executeProxyRequest(
  request: HttpRequest,
  proxyUrl: string,
  accessToken: string | null,
  timeout: number
): Promise<HttpResponse> {
  const url = buildUrl(request);
  const headers = buildHeaders(request);
  const body = getRequestBody(request, headers);

  const proxyBody: ProxyRequestBody = {
    method: request.method,
    url,
    headers,
    body,
    timeout,
  };

  const proxyHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    proxyHeaders['Authorization'] = `Bearer ${accessToken}`;
  }

  const startTime = Date.now();

  try {
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: proxyHeaders,
      body: JSON.stringify(proxyBody),
    });

    const result = await response.json();

    if (result.success && result.data) {
      return result.data as HttpResponse;
    }

    // Handle proxy error response
    return {
      status: 0,
      statusText: 'Error',
      headers: {},
      body: result.error?.message || 'Proxy request failed',
      size: 0,
      time: Date.now() - startTime,
    };
  } catch (err) {
    const endTime = Date.now();

    if (err instanceof Error) {
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: `Proxy request failed: ${err.message}`,
        size: 0,
        time: endTime - startTime,
      };
    }

    throw err;
  }
}

/**
 * HTTP Client class for executing requests
 */
export class HttpClient {
  private config: HttpClientConfig;

  constructor(config: HttpClientConfig = {}) {
    this.config = {
      timeout: 30000,
      ...config,
    };
  }

  /**
   * Set the proxy URL
   */
  setProxyUrl(url: string): void {
    this.config.proxyUrl = url;
  }

  /**
   * Set the access token getter
   */
  setAccessTokenGetter(getter: () => Promise<string | null>): void {
    this.config.getAccessToken = getter;
  }

  /**
   * Execute an HTTP request
   */
  async execute(options: ExecuteRequestOptions): Promise<HttpResponse> {
    const { request, timeout = this.config.timeout || 30000 } = options;

    // Validate request
    if (!request.url) {
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: 'URL is required',
        size: 0,
        time: 0,
      };
    }

    // Validate URL format
    try {
      new URL(request.url);
    } catch {
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: 'Invalid URL format',
        size: 0,
        time: 0,
      };
    }

    // Use direct fetch in Electron, proxy in web browser
    if (isElectron()) {
      return executeDirectRequest(request, timeout);
    }

    // Web browser - use proxy
    const proxyUrl = this.config.proxyUrl || '/api/proxy';
    const accessToken = this.config.getAccessToken
      ? await this.config.getAccessToken()
      : null;

    return executeProxyRequest(request, proxyUrl, accessToken, timeout);
  }
}

// Default singleton instance
let defaultClient: HttpClient | null = null;

/**
 * Get the default HTTP client instance
 */
export function getHttpClient(): HttpClient {
  if (!defaultClient) {
    defaultClient = new HttpClient();
  }
  return defaultClient;
}

/**
 * Configure the default HTTP client
 */
export function configureHttpClient(config: HttpClientConfig): void {
  const client = getHttpClient();
  if (config.proxyUrl) {
    client.setProxyUrl(config.proxyUrl);
  }
  if (config.getAccessToken) {
    client.setAccessTokenGetter(config.getAccessToken);
  }
}

/**
 * Execute a request using the default client
 */
export async function executeRequest(options: ExecuteRequestOptions): Promise<HttpResponse> {
  return getHttpClient().execute(options);
}
